import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Stack,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
} from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useCreateNotificationMutation } from 'services/notification'
import { createNotificationSchema } from './validations'
import { v4 as uuidv4 } from 'uuid'

export type NotificationFieldValues = {
  personalName: string
  phoneNumber: string
  email: string
  messageBody: string
}

type Props = {
  onClose: () => void
}

export const DialogCreateNotification = ({ onClose }: Props) => {
  const { t } = useTranslation()
  const schema = createNotificationSchema(t)

  const [createNotification, { isSuccess, isError }] =
    useCreateNotificationMutation()

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<NotificationFieldValues>({
    defaultValues: {
      personalName: '',
      phoneNumber: '',
      email: '',
      messageBody: '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isSuccess) {
      onClose()
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t, onClose])

  const onSubmit = ({
    personalName,
    phoneNumber,
    email,
    messageBody,
  }: NotificationFieldValues) => {
    createNotification({
      id: uuidv4(),
      personalName,
      phoneNumber,
      email,
      messageBody,
    })
  }

  return (
    <BootstrapDialog open maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
        {t('new-notification')}
      </DialogTitle>

      <DialogCloseButton handleClose={onClose} />

      <DialogContent dividers>
        <Stack spacing={2}>
          <Controller
            name="personalName"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('fullName')}
                  size="small"
                  variant="outlined"
                />
                {errors.personalName && (
                  <FormHelperText error>
                    {errors.personalName?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('phone-number')}
                  size="small"
                  variant="outlined"
                />
                {errors.phoneNumber && (
                  <FormHelperText error>
                    {errors.phoneNumber?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('email-address')}
                  size="small"
                  variant="outlined"
                />
                {errors.email && (
                  <FormHelperText error>{errors.email?.message}</FormHelperText>
                )}
              </Box>
            )}
          />
          <Controller
            name="messageBody"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('notificationMessage')}
                  size="small"
                  multiline
                  minRows={3}
                  variant="outlined"
                />
                {errors.messageBody && (
                  <FormHelperText error>
                    {errors.messageBody?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            autoFocus
            onClick={handleSubmit(onSubmit)}
          >
            {t('create')}
          </Button>
        </Stack>
      </DialogActions>
    </BootstrapDialog>
  )
}
